import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getPages, renderPreview, renderVideo } from "../plugins/utils";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import {
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../../SectionElements/plugins/utils.js";
import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";
import { getRevAllNodes } from "../../../redux/procedureRevisionSlice.js";
import {
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllSubstepsForStep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../../redux/stepSlice";
import DeletePageLayout from "../../../common/DeletePageLayout";
import DeleteContentRenderer from "../../../common/DeleteContentRenderer";
import { useDeleteFunctionality } from "../../../hooks/useDeleteFunctionality";

const ProcedureElementDelete = () => {
  const { sections } = useSelector((state) => state.section);
  const { allRevisionNodes } = useSelector((state) => state.procedureRevision);
  const location = useLocation();
  const dispatch = useDispatch();

  // Determine delete type from location state (with safety check)
  const {
    deleteType = "SECTION", // Can be: SECTION, STEP, SUBSTEP, CHILD
    revision,
    partNumber,
    type,
    partId,
    revisionId,
    fromSectionId,
    fromStepId,
    fromSubstepId,
    fromNodeId,
    title,
    status,
    description,
    elementType,
  } = location.state || {};

  // Use common delete functionality
  const {
    isLoading,
    isModalOpen,
    isConfirmModalOpen,
    comment,
    snackbar,
    setModalOpen,
    setConfirmModalOpen,
    setComment,
    showSnackbar,
    handleCloseSnackbar,
    handleConfirm,
    handleConfirmSave,
  } = useDeleteFunctionality(location.state);

  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  let substepCounter = 1;
  let stepCounter = 1;
  const [isProcedureSelected, setIsProcedureSelected] = useState(false);
  let richTextsCounter = {};
  let attachmentCounter = 1;

  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  const [stepsBySectionPreview, setStepsBySectionPreview] = useState(
    stepsBySection || {}
  );
  const [selectedView, setSelectedView] = useState("new");
  const [previewSections, setPreviewSections] = useState([...sections]);
  const [allSubsteps, setAllSubsteps] = useState(substepsByStep || {});

  // Filter logic for different delete types
  useEffect(() => {
    const sectionToDeleteId =
      location.state?.section?.section_tree_node_id || fromSectionId;
    const stepToDeleteId =
      location.state?.step?.step_tree_node_id || fromStepId;
    const substepToDeleteId =
      location.state?.substep?.sub_step_tree_node_id || fromSubstepId;

    let filteredSections = [...sections];

    if (selectedView !== "original") {
      switch (deleteType) {
        case "SECTION":
          // Remove the section
          if (sectionToDeleteId) {
            filteredSections = filteredSections.filter(
              (sec) => sec.section_tree_node_id !== sectionToDeleteId
            );
            setStepsBySectionPreview((prev) => {
              const newState = { ...prev };
              delete newState[sectionToDeleteId];
              return newState;
            });
          }
          break;

        case "STEP":
          // Remove the step from its section
          if (sectionToDeleteId && stepToDeleteId) {
            setStepsBySectionPreview((prev) => {
              const baseSteps =
                (prev[sectionToDeleteId] && prev[sectionToDeleteId].length > 0
                  ? prev[sectionToDeleteId]
                  : stepsBySection[sectionToDeleteId]) || [];
              const updatedSteps = baseSteps.filter(
                (step) => step.step_tree_node_id !== stepToDeleteId
              );
              return {
                ...prev,
                [sectionToDeleteId]: updatedSteps,
              };
            });
          }
          break;

        case "SUBSTEP":
          // Remove the substep from its step
          if (stepToDeleteId && substepToDeleteId) {
            setAllSubsteps((prev) => {
              const updatedSubsteps = (prev[stepToDeleteId] || []).filter(
                (substep) => substep.sub_step_tree_node_id !== substepToDeleteId
              );
              return {
                ...prev,
                [stepToDeleteId]: updatedSubsteps,
              };
            });
          }
          break;

        case "CHILD":
          // For child nodes, filtering is handled in the rendering logic
          // based on fromNodeId and elementType
          break;

        default:
          break;
      }
    }

    setPreviewSections(filteredSections);

    // Restore original view
    if (selectedView === "original") {
      if (sectionToDeleteId) {
        const originalSteps = [...(stepsBySection[sectionToDeleteId] || [])];
        setStepsBySectionPreview((prev) => ({
          ...prev,
          [sectionToDeleteId]: originalSteps,
        }));
      }
      setAllSubsteps(substepsByStep);
    }
  }, [selectedView, location.state, sections, deleteType]);

  // Fetch data for original view
  useEffect(() => {
    if (fromSectionId) {
      dispatch(
        getRevAllNodes({
          partId: partId,
          revisionId: revisionId,
        })
      );
    }
  }, [dispatch, partId, revisionId, fromSectionId]);

  // Refresh step and substep data for child deletion
  useEffect(() => {
    if (deleteType === "CHILD" && fromStepId && fromSectionId) {
      // Refresh step-level children
      dispatch(
        getAllRichTextsForStep({
          partId,
          revisionId,
          sectionId: fromSectionId,
          stepId: fromStepId,
        })
      );
      dispatch(
        getAllFormsForStep({
          partId,
          revisionId,
          sectionId: fromSectionId,
          stepId: fromStepId,
        })
      );
      dispatch(
        getAllVideosForStep({
          partId,
          revisionId,
          sectionId: fromSectionId,
          stepId: fromStepId,
        })
      );
      dispatch(
        getAllAttachmentsForStep({
          partId,
          revisionId,
          sectionId: fromSectionId,
          stepId: fromStepId,
        })
      );
      dispatch(
        getAllCanvasesForStep({
          partId,
          revisionId,
          sectionId: fromSectionId,
          stepId: fromStepId,
        })
      );

      // Refresh substeps
      dispatch(
        getAllSubstepsForStep({
          partId,
          revisionId,
          sectionId: fromSectionId,
          stepId: fromStepId,
        })
      ).then(() => {
        const effectiveSubstepId =
          fromSubstepId === "null" || fromSubstepId === null
            ? null
            : fromSubstepId;
        if (effectiveSubstepId) {
          dispatch(
            getAllRichTextsForSubstep({
              partId,
              revisionId,
              sectionId: fromSectionId,
              stepId: fromStepId,
              substepId: effectiveSubstepId,
            })
          );
          dispatch(
            getAllFormsForSubstep({
              partId,
              revisionId,
              sectionId: fromSectionId,
              stepId: fromStepId,
              substepId: effectiveSubstepId,
            })
          );
          dispatch(
            getAllVideosForSubstep({
              partId,
              revisionId,
              sectionId: fromSectionId,
              stepId: fromStepId,
              substepId: effectiveSubstepId,
            })
          );
          dispatch(
            getAllAttachmentsForSubstep({
              partId,
              revisionId,
              sectionId: fromSectionId,
              stepId: fromStepId,
              substepId: effectiveSubstepId,
            })
          );
          dispatch(
            getAllCanvasesForSubstep({
              partId,
              revisionId,
              sectionId: fromSectionId,
              stepId: fromStepId,
              substepId: effectiveSubstepId,
            })
          );
        }
      });
    }
  }, [
    dispatch,
    partId,
    revisionId,
    fromSectionId,
    fromStepId,
    fromSubstepId,
    deleteType,
  ]);

  // Filter functions for child deletion
  const getFilteredStepItems = (stepId, step) => {
    const combinedItems = [
      ...(richTexts[stepId] || []).map((item) => ({
        ...item,
        type: "richText",
      })),
      ...(forms[stepId] || []).map((item) => ({ ...item, type: "form" })),
      ...(attachments[stepId] || []).map((item) => ({
        ...item,
        type: "attachment",
      })),
      ...(canvases[stepId] || []).map((item) => ({ ...item, type: "canvas" })),
      ...(videos[stepId] || []).map((item) => ({ ...item, type: "video" })),
      ...(step.attachments || []).map((item) => ({
        ...item,
        type: "attachment",
      })),
      ...(step.videos || []).map((item) => ({ ...item, type: "video" })),
      ...(step.forms || []).map((item) => ({ ...item, type: "form" })),
      ...(step.canvases || []).map((item) => ({ ...item, type: "canvas" })),
      ...(step.richTexts || []).map((item) => ({ ...item, type: "richText" })),
    ];

    // Only filter for "new" view when deleting a CHILD node
    if (selectedView !== "new" || deleteType !== "CHILD" || !fromNodeId) {
      return combinedItems;
    }

    // If deleting from a substep, don't filter step-level items
    if (fromSubstepId && fromSubstepId !== "null" && fromSubstepId !== null) {
      return combinedItems;
    }
    const filtered = combinedItems
      .map((item) => {
        if (item.type === "attachment" && Array.isArray(item.attachments)) {
          const wrapperShouldDelete =
            elementType === "attachment" &&
            item.attachment_tree_node_id === fromNodeId;
          if (wrapperShouldDelete) return null;

          if (elementType === "attachment") {
            const pruned = item.attachments.filter((att) => {
              const shouldDelete = att.attachment_tree_node_id === fromNodeId;
              return !shouldDelete;
            });
            if (pruned.length === 0) return null;
            return { ...item, attachments: pruned };
          }
          return item;
        }

        const shouldDelete = (() => {
          switch (elementType) {
            case "rich-text":
              const richTextMatch =
                item.rich_text_node_id === fromNodeId ||
                item.rich_text_tree_node_id === fromNodeId;
              return richTextMatch;
            case "attachment":
              return item.attachment_tree_node_id === fromNodeId;
            case "video":
              const videoMatch =
                item.video_node_id === fromNodeId ||
                item.video_tree_node_id === fromNodeId;
              return videoMatch;
            case "form":
              const formMatch =
                item.form_node_id === fromNodeId ||
                item.form_tree_node_id === fromNodeId;
              return formMatch;
            case "canvas":
              const canvasMatch =
                item.canvas_node_id === fromNodeId ||
                item.canvas_tree_node_id === fromNodeId ||
                item.canvas_tree_node_ref === fromNodeId;
              return canvasMatch;
            default:
              return false;
          }
        })();

        if (shouldDelete) {
        }
        return shouldDelete ? null : item;
      })
      .filter(Boolean);
    return filtered;
  };

  const getFilteredSubstepItems = (substepId) => {
    const combinedItems = [
      ...(richTexts[substepId] || []).map((item) => ({
        ...item,
        type: "richText",
      })),
      ...(forms[substepId] || []).map((item) => ({ ...item, type: "form" })),
      ...(attachments[substepId] || []).map((item) => ({
        ...item,
        type: "attachment",
      })),
      ...(canvases[substepId] || []).map((item) => ({
        ...item,
        type: "canvas",
      })),
      ...(videos[substepId] || []).map((item) => ({ ...item, type: "video" })),
    ];

    // Only filter if: new view, deleting CHILD, fromNodeId exists, and deleting from THIS substep
    if (
      selectedView !== "new" ||
      deleteType !== "CHILD" ||
      !fromNodeId ||
      fromSubstepId !== substepId
    ) {
      return combinedItems;
    }
    return combinedItems
      .map((item) => {
        if (item.type === "attachment" && Array.isArray(item.attachments)) {
          const wrapperShouldDelete =
            elementType === "attachment" &&
            item.attachment_tree_node_id === fromNodeId;
          if (wrapperShouldDelete) return null;

          if (elementType === "attachment") {
            const pruned = item.attachments.filter(
              (att) => att.attachment_tree_node_id !== fromNodeId
            );
            if (pruned.length === 0) return null;
            return { ...item, attachments: pruned };
          }
          return item;
        }

        const shouldDelete = (() => {
          switch (elementType) {
            case "rich-text":
              return (
                item.rich_text_node_id === fromNodeId ||
                item.rich_text_tree_node_id === fromNodeId
              );
            case "attachment":
              return item.attachment_tree_node_id === fromNodeId;
            case "video":
              return (
                item.video_node_id === fromNodeId ||
                item.video_tree_node_id === fromNodeId
              );
            case "form":
              return (
                item.form_node_id === fromNodeId ||
                item.form_tree_node_id === fromNodeId
              );
            case "canvas":
              return (
                item.canvas_node_id === fromNodeId ||
                item.canvas_tree_node_id === fromNodeId ||
                item.canvas_tree_node_ref === fromNodeId
              );
            default:
              return false;
          }
        })();

        return shouldDelete ? null : item;
      })
      .filter(Boolean);
  };

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const onConfirmSave = () => {
    handleConfirmSave(deleteType, allRevisionNodes);
  };

  // Safety check for location.state - AFTER all hooks
  if (!location.state) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>Error: No data provided</h2>
        <p>Please navigate to this page from a valid delete action.</p>
        <p style={{ marginTop: "20px" }}>
          <button
            onClick={() => window.history.back()}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Go Back
          </button>
        </p>
      </div>
    );
  }

  return (
    <DeletePageLayout
      selectedView={selectedView}
      setSelectedView={setSelectedView}
      onConfirm={handleConfirm}
      type={type}
      partNumber={partNumber}
      status={status}
      revision={revision}
      isProcedureSelected={isProcedureSelected}
      isLoading={isLoading}
      itemName={title}
      isModalOpen={isModalOpen}
      isConfirmModalOpen={isConfirmModalOpen}
      setModalOpen={setModalOpen}
      setConfirmModalOpen={setConfirmModalOpen}
      comment={comment}
      setComment={setComment}
      onConfirmSave={onConfirmSave}
      snackbar={snackbar}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <div className="header-spacing" style={{ padding: "20px" }}>
        <div style={{ marginTop: "20px" }}>
          {previewSections
            .sort((a, b) => {
              const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
              const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
              const orderCompare = compareSiblingOrders(orderA, orderB);
              if (orderCompare !== 0) return orderCompare;
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateB - dateA;
            })
            .map((section, index) => {
              const stepsToRender =
                stepsBySectionPreview[section.section_tree_node_id] || [];
              return (
                <div
                  className="header-spacing"
                  key={section.section_tree_node_id}
                  style={{ margin: "20px 0" }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <h2 style={{ margin: 0 }}>Section {index + 1}</h2>
                  </label>
                  <hr />
                  <hr />
                  <div>{section.section_name}</div>
                  <div>{section.short_description}</div>

                  {/* Steps for this section */}
                  {(stepsToRender || []).map((step, stepIndex) => {
                    const stepId = step.step_tree_node_id;
                    stepCounter++;
                    const stepsBeforeThisSection = previewSections
                      .slice(0, index)
                      .reduce(
                        (acc, s) =>
                          acc +
                          (stepsBySectionPreview[s.section_tree_node_id]
                            ?.length ||
                            stepsBySection[s.section_tree_node_id]?.length ||
                            0),
                        0
                      );
                    const globalStepNumber =
                      stepsBeforeThisSection + stepIndex + 1;

                    richTextsCounter[stepId] = richTextCounter;
                    const filteredItems = getFilteredStepItems(
                      stepId,
                      step
                    ).filter(
                      (item) =>
                        !item.substep_id_ref && !item.sub_step_tree_node_id
                    );

                    filteredItems.sort((a, b) => {
                      const orderA =
                        a.sibling_order || a.procedure_tree?.sibling_order;
                      const orderB =
                        b.sibling_order || b.procedure_tree?.sibling_order;
                      const orderCompare = compareSiblingOrders(orderA, orderB);
                      if (orderCompare !== 0) return orderCompare;
                      const dateA = new Date(a.created_at);
                      const dateB = new Date(b.created_at);
                      return dateB - dateA;
                    });

                    return (
                      <div
                        key={stepId}
                        className="header-spacing"
                        style={{ marginTop: "20px" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              gap: "8px",
                              cursor: "pointer",
                            }}
                          >
                            <h2 style={{ margin: 0, color: "#333" }}>
                              Step {globalStepNumber}
                            </h2>
                          </label>
                          <div
                            style={{
                              color: "#888",
                              fontSize: "14px",
                              fontWeight: "normal",
                            }}
                          >
                            Section {index + 1} &gt; Step {globalStepNumber}
                          </div>
                        </div>
                        <hr />
                        <p>{step.short_description || ""}</p>
                        <p>
                          <p>
                            {structuredClone(step.step_details)
                              .sort(
                                (a, b) =>
                                  new Date(a.created_at) -
                                  new Date(b.created_at)
                              )
                              .map((myStep, idx) => {
                                const partNum =
                                  myStep.part_info?.part_number || "";
                                const desc =
                                  myStep.part_info?.description || "";
                                const qtyText =
                                  myStep.qty && myStep.qty !== 0
                                    ? `Qty ${myStep.qty}`
                                    : "";
                                const partText = partNum ? ` (${partNum})` : "";
                                return (
                                  <span key={idx}>
                                    {myStep.action}
                                    {partText} <RenderHtml content={desc} />{" "}
                                    {qtyText}&nbsp;
                                  </span>
                                );
                              })}
                          </p>
                        </p>

                        <h4>
                          {step.bom_table_title_info?.category_name || ""}
                        </h4>

                        <table className="table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>
                                {
                                  step.part_number_column_title_ref_info
                                    ?.category_name
                                }{" "}
                              </th>
                              <th>Description</th>
                              <th>Qty Per</th>
                              <th>Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {step.parts_required.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.part_info.type_info.name}</td>
                                <td>{row.part_info.part_number}</td>
                                <td>
                                  <RenderHtml
                                    content={row.part_info.description}
                                  />
                                </td>
                                <td>{row.qty === 0 ? "" : row.qty}</td>
                                <td>{row.part_info?.unit_info?.unit_name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Step Content using common renderer */}
                        {filteredItems.map((item) => {
                          if (item.substep_id_ref || item.sub_step_tree_node_id)
                            return null;
                          const counters = {
                            richTextCounter: richTextCounter++,
                            videoCounter: videoCounter++,
                            formCounter: formCounter++,
                            canvasCounter: canvasCounter++,
                            attachmentCounter: attachmentCounter++,
                          };
                          return (
                            <DeleteContentRenderer
                              key={
                                item.rich_text_tree_node_id ||
                                item.video_tree_node_id ||
                                item.form_tree_node_id ||
                                item.canvas_tree_node_id ||
                                item.attachment_tree_node_id ||
                                Math.random()
                              }
                              item={item}
                              sectionIndex={index}
                              globalStepNumber={globalStepNumber}
                              counters={counters}
                            />
                          );
                        })}

                        {/* Substeps */}
                        {(allSubsteps[stepId] || []).map(
                          (substep, substepIndex) => {
                            const substepId = substep.sub_step_tree_node_id;
                            const filteredItems =
                              getFilteredSubstepItems(substepId);

                            filteredItems.sort((a, b) => {
                              const orderA =
                                a.sibling_order ||
                                a.procedure_tree?.sibling_order;
                              const orderB =
                                b.sibling_order ||
                                b.procedure_tree?.sibling_order;
                              const orderCompare = compareSiblingOrders(
                                orderA,
                                orderB
                              );
                              if (orderCompare !== 0) return orderCompare;
                              const dateA = new Date(a.created_at);
                              const dateB = new Date(b.created_at);
                              return dateB - dateA;
                            });

                            const substepLetter = String.fromCharCode(
                              65 + substepIndex
                            );
                            const globalSubstepLabel = `${substepLetter}`;

                            return (
                              <div
                                className="header-spacing"
                                key={substepId}
                                style={{ marginTop: "20px" }}
                              >
                                <div>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      <h2 style={{ margin: 0, color: "#333" }}>
                                        Substep {globalStepNumber}
                                        {substepLetter}
                                      </h2>
                                    </label>
                                    <div
                                      style={{
                                        color: "#888",
                                        fontSize: "14px",
                                        fontWeight: "normal",
                                      }}
                                    >
                                      Section {index + 1} &gt; Step{" "}
                                      {globalStepNumber} &gt; Substep{" "}
                                      {globalStepNumber}
                                      {substepLetter}
                                    </div>
                                  </div>
                                  <div>{substep.short_description}</div>
                                </div>

                                {/* Substep Content using common renderer */}
                                {filteredItems.map((item) => {
                                  const counters = {
                                    richTextCounter: richTextCounter++,
                                    videoCounter: videoCounter++,
                                    formCounter: formCounter++,
                                    canvasCounter: canvasCounter++,
                                    attachmentCounter: attachmentCounter++,
                                  };
                                  return (
                                    <DeleteContentRenderer
                                      key={
                                        item.rich_text_tree_node_id ||
                                        item.video_tree_node_id ||
                                        item.form_tree_node_id ||
                                        item.canvas_tree_node_id ||
                                        item.attachment_tree_node_id ||
                                        Math.random()
                                      }
                                      item={item}
                                      sectionIndex={index}
                                      globalStepNumber={globalStepNumber}
                                      globalSubstepLabel={globalSubstepLabel}
                                      counters={counters}
                                    />
                                  );
                                })}
                              </div>
                            );
                          }
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </div>
    </DeletePageLayout>
  );
};

export default ProcedureElementDelete;
